import { useQuery } from '@apollo/client';
import { faMobileScreen, faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import { Outlet, useOutletContext } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { Role } from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import type { OutcropVomsTabQuery } from '~/apollo/generated/v4/graphql';
import { RoleSecured } from '~/components/auth/RoleSecured';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { Tooltip } from '~/components/common/Tooltip';
import { NavTabs } from '~/components/navigation/NavTabs';
import { useRouteParam } from '~/hooks/routing';
import {
  outcropTourDetailRoute,
  outcropTourRoute,
  outcropVirtualOutcropsTabRoute,
} from '~/paths';
import { cn } from '~/utils/common';
import { isMini } from '~/utils/modules/vom';

const OUTCROP_VOMS_TAB = graphql(`
  query OutcropVomsTab($id: ID!) {
    outcropGet(id: $id) {
      id
      outcropTours {
        id
      }
      virtualOutcropModels {
        id
        name
        priority
        interpreted
        acquisitionType
      }
    }
  }
`);

type Outcrop = NonNullable<OutcropVomsTabQuery['outcropGet']>;
type OutletContext = {
  outcropId: number;
  linkedVoms: Outcrop['virtualOutcropModels'];
};

export default function OutcropVomsRoute() {
  return (
    <ApolloProviderV4>
      <PageInner />
    </ApolloProviderV4>
  );
}

function PageInner() {
  const outcropId = useRouteParam('outcropId', parseInt);

  const { data, loading } = useQuery(OUTCROP_VOMS_TAB, {
    variables: { id: outcropId },
  });

  const outcrop = data?.outcropGet;
  const vomList = outcrop?.virtualOutcropModels ?? [];
  const voms = R.sortWith(
    [R.ascend(vom => vom.priority ?? Infinity), R.ascend(vom => vom.name)],
    vomList,
  );

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;
  if (!voms.length) {
    return (
      <p>This outcrop does not have any virtual outcrops associated with it.</p>
    );
  }

  const outletContext: OutletContext = {
    outcropId,
    linkedVoms: voms,
  };

  const hasMultipleVoms = voms.length > 1;
  const hasTours = outcrop.outcropTours.length > 0;
  const showTabs = hasMultipleVoms || hasTours;

  const tourPath = (() => {
    const firstTour = outcrop.outcropTours.at(0);
    if (!firstTour) return null;

    if (outcrop.outcropTours.length === 1) {
      return outcropTourDetailRoute(outcropId, parseInt(firstTour.id));
    } else {
      return outcropTourRoute(outcropId);
    }
  })();

  return (
    <>
      {showTabs && (
        <NavTabs className="mb-2">
          {voms.map(vom => (
            <NavTabs.Tab
              key={vom.id}
              to={outcropVirtualOutcropsTabRoute(outcropId, parseInt(vom.id))}
              className={cn({
                'font-extrabold': vom.interpreted,
              })}
            >
              {vom.name}
              {vom.interpreted && (
                <Tooltip message="Interpreted">
                  <sup>
                    <FontAwesomeIcon
                      icon={faStar}
                      className="text-warning text-sm mx-1"
                    />
                  </sup>
                </Tooltip>
              )}
              {isMini(vom) && (
                <Tooltip message="Mini-Model">
                  <sup>
                    <FontAwesomeIcon
                      icon={faMobileScreen}
                      className="text-warning text-sm mx-1"
                    />
                  </sup>
                </Tooltip>
              )}
            </NavTabs.Tab>
          ))}
          {hasTours && tourPath && (
            <RoleSecured>
              <NavTabs.Tab to={tourPath}>Outcrop Tour</NavTabs.Tab>
            </RoleSecured>
          )}
        </NavTabs>
      )}

      <Outlet context={outletContext} />
    </>
  );
}

export function useOutcropVomsOutletContext() {
  return useOutletContext<OutletContext>();
}
